import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { isPlaceholderEntityName } from "score-common";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveOrg, useActiveShow } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useSortedRows } from "../hooks/useSortedRows";
import { useNextRides } from "../hooks/useNextRides";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import NextRideCell from "../components/NextRideCell";
import RideHistoryDrawer from "../components/RideHistoryDrawer";

// #507: replace the Horses placeholder with a real search + detail screen.
// Same pattern as PeopleManager.jsx minus the personType tabs — horses have
// no such split, just a single org-scoped list (score-api HorsesApi is
// read-only; FVD owns horse data via reconcile, card #356).
export default function HorseManager() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.horses"));
  const scoreApi = useScoreApi();
  const { activeOrgId } = useActiveOrg();
  const { activeShowId } = useActiveShow();
  // #558 — each row shows the horse's next ride in the active show.
  const { nextByHorse } = useNextRides(activeShowId);

  const [horses, setHorses] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // #3160 (same pattern as PeopleManager #776 / ClassManager #2700) -- the
    // org context (X-Org-Id) isn't wired into scoreApi until activeOrgId
    // resolves. Fetching before then returns an empty/unscoped list and
    // flashed "No horses found"; stay on the skeleton (don't flip loading
    // off) until the org-ready run, and refetch when the active org changes.
    if (!activeOrgId) {
      setLoading(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const list = unwrap(await scoreApi.horses.listHorses());
        if (!cancelled) setHorses(list);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scoreApi, activeOrgId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // #585 — hide FVD placeholder rows ("***Break***") already seeded before
    // the reconcile-side filter landed.
    const base = horses.filter((h) => !isPlaceholderEntityName(h.name));
    if (!q) return base;
    return base.filter((h) => (h.name || "").toLowerCase().includes(q));
  }, [horses, search]);

  // #557 — sortable columns over the search-filtered rows.
  const { sorted, sortKey, sortDir, toggleSort } = useSortedRows(
    filtered,
    { name: (h) => h.name, breed: (h) => h.breed, usefId: (h) => h.usefId },
    "name",
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("horses.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("horses.loadError")}
        </Alert>
      )}

      <TextField
        size="small"
        fullWidth
        sx={{ mb: 2, maxWidth: 360 }}
        placeholder={t("horses.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary">{t("horses.empty")}</Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  { key: "name", labelKey: "horses.columns.name" },
                  { key: "breed", labelKey: "horses.columns.breed" },
                  { key: "usefId", labelKey: "horses.columns.usefId" },
                ].map((col) => (
                  <TableCell key={col.key} sortDirection={sortKey === col.key ? sortDir : false}>
                    <TableSortLabel
                      active={sortKey === col.key}
                      direction={sortKey === col.key ? sortDir : "asc"}
                      onClick={() => toggleSort(col.key)}
                    >
                      {t(col.labelKey)}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell>{t("horses.columns.nextRide")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((horse) => (
                <TableRow
                  key={horse.id}
                  hover
                  onClick={() => setSelected(horse)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{horse.name}</TableCell>
                  <TableCell>{horse.breed || "-"}</TableCell>
                  <TableCell>{horse.usefId || "-"}</TableCell>
                  <TableCell>
                    <NextRideCell
                      nextRide={nextByHorse[horse.id]}
                      ringLabel={t("nextRide.ring")}
                      emptyLabel={t("nextRide.none")}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <RideHistoryDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? selected.name : ""}
        infoItems={
          selected
            ? [
                { label: t("horses.columns.breed"), value: selected.breed },
                { label: t("horses.columns.usefId"), value: selected.usefId },
              ]
            : []
        }
        filterKey="horseId"
        subjectId={selected?.id}
      />
    </Box>
  );
}
